import React, { useCallback } from 'react';

import { Stack, ButtonGroup, Button } from '@gogame/components';
import { useIsAllowed } from '@gogame/permissions';
import { Screen, useScreenModalV2, useParams } from '@gogame/screens';

import { useLoader } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Loader';
import { ConnectedAvatars } from 'scenes/MarketingPlanning/MarketingPlanningWizard/components/Socket';
import {
  useDiscardMarketingPlan,
  useSaveDraftMarketingPlan,
  useFinalizeMarketingPlan,
} from 'scenes/MarketingPlanning/MarketingPlanningWizard/hooks';

import ConfirmDiscardDialog from './ConfirmDiscardDialog';
import ConfirmFinalizedDialog from './ConfirmFinalizedDialog';
import ConfirmSaveDialog from './ConfirmSaveDialog';

const MARKETING_PLANNING_WRITING_ADMIN = 12;

const MarketingPlanningActions = ({ hasUnsavedChanges }) => {
  const showSaveDialog = useScreenModalV2(ConfirmSaveDialog);
  const showDiscardDialog = useScreenModalV2(ConfirmDiscardDialog);
  const showFinalizedDialog = useScreenModalV2(ConfirmFinalizedDialog);
  const { processAsync } = useLoader();
  const isAllowed = useIsAllowed();
  const params = useParams();
  const discardMarketingPlan = useDiscardMarketingPlan(processAsync);
  const saveDraftMarketingPlan = useSaveDraftMarketingPlan(processAsync);
  const finalizeDraftMarketingPlan = useFinalizeMarketingPlan(processAsync);

  const handleDiscardMarketingPlan = useCallback(async () => {
    await showDiscardDialog();
    await discardMarketingPlan({
      year: params.year,
      month: params.month,
    });
  }, [showDiscardDialog, discardMarketingPlan, params.year, params.month]);

  const handleSaveDraftMarketingPlan = useCallback(async () => {
    const { comment } = await showSaveDialog();

    await saveDraftMarketingPlan({
      year: params.year,
      month: params.month,
      comment,
    });
  }, [showSaveDialog, saveDraftMarketingPlan, params.year, params.month]);

  const handleFinalizeMarketingPlan = useCallback(async () => {
    const { comment } = await showFinalizedDialog();

    await finalizeDraftMarketingPlan({
      year: params.year,
      month: params.month,
      comment,
    });
  }, [
    showFinalizedDialog,
    finalizeDraftMarketingPlan,
    params.year,
    params.month,
  ]);

  return (
    <React.Fragment>
      <Screen.Toolbar.Actions>
        <Stack
          spacing={2}
          direction={'row'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <ConnectedAvatars />
          <ButtonGroup>
            <Button
              variant={'outlined'}
              onClick={handleSaveDraftMarketingPlan}
              disabled={!hasUnsavedChanges}
            >
              Save draft
            </Button>
            <Button
              onClick={handleDiscardMarketingPlan}
              disabled={!hasUnsavedChanges}
            >
              Discard changes
            </Button>
          </ButtonGroup>
          <Button
            variant={'contained'}
            onClick={handleFinalizeMarketingPlan}
            disabled={!isAllowed(MARKETING_PLANNING_WRITING_ADMIN)}
          >
            Finalize Plan
          </Button>
        </Stack>
      </Screen.Toolbar.Actions>
    </React.Fragment>
  );
};

export default MarketingPlanningActions;
